import { useState, useRef, useCallback, useEffect } from 'react';
import {
  FileText,
  Plus,
  Trash2,
  GripVertical,
  Download,
  Save,
  Image,
  Palette,
  X,
  ChevronDown,
  FilePlus,
} from 'lucide-react';
import useInvoice, { CURRENCIES, PAYMENT_TERMS } from '@/hooks/useInvoice';
import { generateInvoicePDF } from '@/lib/invoicePdf';
import { useFileDialog } from '@/hooks/useFileDialog';
import { ProGate } from '@/components/ProBadge';
import { usageCapApplies, hasReachedLimit, incrementUsage } from '../lib/webUsage';
import FreeTierBanner from '../components/FreeTierBanner';

export default function Invoice() {
  const {
    invoice,
    totals,
    drafts,
    formatCurrency,
    updateField,
    updateLineItem,
    addLineItem,
    removeLineItem,
    reorderLineItems,
    uploadLogo,
    removeLogo,
    saveDraft,
    newInvoice,
    loadDraft,
  } = useInvoice();

  const { openFile, saveFile: saveFileDialog } = useFileDialog();
  const [exporting, setExporting] = useState(false);
  const [showFreeTierModal, setShowFreeTierModal] = useState(false);
  const [showDrafts, setShowDrafts] = useState(false);
  const dragItemRef = useRef(null);
  const dragOverRef = useRef(null);

  const handleExport = useCallback(async () => {
    if (usageCapApplies() && hasReachedLimit()) {
      setShowFreeTierModal(true);
      return;
    }
    setExporting(true);
    try {
      const pdfBytes = await generateInvoicePDF(invoice, totals);
      const clientSlug = invoice.clientName
        ? invoice.clientName.replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_')
        : 'client';
      const fileName = `${invoice.invoiceNumber}_${clientSlug}.pdf`;
      await saveFileDialog(pdfBytes, fileName);
      if (usageCapApplies()) incrementUsage();
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  }, [invoice, totals, saveFileDialog]);

  const handleDragStart = (index) => {
    dragItemRef.current = index;
  };

  const handleDragEnter = (index) => {
    dragOverRef.current = index;
  };

  const handleDragEnd = () => {
    if (dragItemRef.current !== null && dragOverRef.current !== null && dragItemRef.current !== dragOverRef.current) {
      reorderLineItems(dragItemRef.current, dragOverRef.current);
    }
    dragItemRef.current = null;
    dragOverRef.current = null;
  };

  return (
    <ProGate feature="invoice">
    <div className="min-h-screen bg-gray-100">
      {/* Top toolbar */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText size={22} className="text-brand-blue" />
            <h1 className="text-lg font-semibold text-gray-900">Invoice Builder</h1>
          </div>

          <div className="flex items-center gap-2">
            {/* Drafts dropdown */}
            {drafts.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setShowDrafts(!showDrafts)}
                  className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Drafts ({drafts.length})
                  <ChevronDown size={14} />
                </button>
                {showDrafts && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[200px]">
                    {drafts.map((d) => (
                      <button
                        key={d.invoiceNumber}
                        onClick={() => { loadDraft(d.invoiceNumber); setShowDrafts(false); }}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex justify-between items-center"
                      >
                        <span className="font-medium">{d.invoiceNumber}</span>
                        <span className="text-gray-400 text-xs">
                          {d.clientName || 'No client'}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button
              onClick={newInvoice}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FilePlus size={16} />
              New
            </button>

            <button
              onClick={saveDraft}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Save size={16} />
              Save Draft
            </button>

            <button
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-brand-blue hover:bg-brand-dark rounded-lg transition-colors disabled:opacity-50"
            >
              <Download size={16} />
              {exporting ? 'Exporting...' : 'Export PDF'}
            </button>
          </div>
        </div>
      </div>

      {/* Invoice form/preview */}
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Accent color bar */}
          <div className="h-2" style={{ backgroundColor: invoice.accentColor }} />

          <div className="p-8 md:p-12">
            {/* Header section */}
            <div className="flex justify-between items-start mb-10">
              {/* Company info (left) — constrained width */}
              <div className="max-w-[320px]">
                {/* Logo upload with resize */}
                <div className="mb-4">
                  {invoice.companyLogo ? (
                    <ResizableLogo
                      logo={invoice.companyLogo}
                      onRemove={removeLogo}
                      onResize={(w, h) => updateField('companyLogo', { ...invoice.companyLogo, width: w, height: h })}
                    />
                  ) : (
                    <button
                      onClick={async () => {
                        const result = await openFile('image/*');
                        if (result) {
                          const file = new File([result.buffer], result.name, { type: 'image/*' });
                          uploadLogo(file);
                        }
                      }}
                      className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-400 hover:border-brand-blue hover:text-brand-blue transition-colors"
                    >
                      <Image size={20} />
                      <span className="text-sm">Add Logo</span>
                    </button>
                  )}
                </div>

                <EditableField
                  value={invoice.companyName}
                  onChange={(v) => updateField('companyName', v)}
                  placeholder="Your Company Name"
                  className="text-xl font-bold text-gray-900"
                />
                <AddressField
                  lines={invoice.companyAddress}
                  onChange={(v) => updateField('companyAddress', v)}
                  placeholders={['1101 Broadway', 'New York, NY 10010']}
                />
                <EditableField
                  value={invoice.companyPhone}
                  onChange={(v) => updateField('companyPhone', v)}
                  placeholder="(555) 123-4567"
                  className="text-sm text-gray-700"
                />
                <EditableField
                  value={invoice.companyEmail}
                  onChange={(v) => updateField('companyEmail', v)}
                  placeholder="email@company.com"
                  className="text-sm text-gray-700"
                />
                <EditableField
                  value={invoice.companyWebsite}
                  onChange={(v) => updateField('companyWebsite', v)}
                  placeholder="www.company.com"
                  className="text-sm text-gray-700"
                />
              </div>

              {/* Invoice title + details (right) */}
              <div className="text-right">
                <h2
                  className="text-3xl font-bold tracking-wide mb-4"
                  style={{ color: invoice.accentColor }}
                >
                  INVOICE
                </h2>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-end gap-2">
                    <span className="text-gray-500">#</span>
                    <EditableField
                      value={invoice.invoiceNumber}
                      onChange={(v) => updateField('invoiceNumber', v)}
                      className="text-right font-semibold text-gray-900"
                    />
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <span className="text-gray-500">Date:</span>
                    <input
                      type="date"
                      value={invoice.invoiceDate}
                      onChange={(e) => updateField('invoiceDate', e.target.value)}
                      className="text-right text-gray-900 border-0 border-b border-transparent hover:border-gray-300 focus:border-brand-blue focus:ring-0 bg-transparent text-sm p-0"
                    />
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <span className="text-gray-500">Due:</span>
                    <input
                      type="date"
                      value={invoice.dueDate}
                      onChange={(e) => updateField('dueDate', e.target.value)}
                      className="text-right text-gray-900 border-0 border-b border-transparent hover:border-gray-300 focus:border-brand-blue focus:ring-0 bg-transparent text-sm p-0"
                    />
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <span className="text-gray-500">Terms:</span>
                    <select
                      value={invoice.paymentTerms}
                      onChange={(e) => updateField('paymentTerms', e.target.value)}
                      className="text-right text-gray-900 border-0 bg-transparent text-sm p-0 cursor-pointer focus:ring-0"
                    >
                      {PAYMENT_TERMS.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  {/* Currency + accent color */}
                  <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100 mt-2">
                    <select
                      value={invoice.currency}
                      onChange={(e) => updateField('currency', e.target.value)}
                      className="text-xs text-gray-400 border-0 bg-transparent p-0 cursor-pointer focus:ring-0"
                    >
                      {CURRENCIES.map((c) => (
                        <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
                      ))}
                    </select>
                    <label className="flex items-center gap-1 cursor-pointer" title="Accent color">
                      <Palette size={14} className="text-gray-400" />
                      <input
                        type="color"
                        value={invoice.accentColor}
                        onChange={(e) => updateField('accentColor', e.target.value)}
                        className="w-5 h-5 border-0 p-0 cursor-pointer rounded"
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Client section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
              <div className="max-w-[320px]">
                <label
                  className="text-xs font-bold uppercase tracking-wider mb-2 block"
                  style={{ color: invoice.accentColor }}
                >
                  Bill To
                </label>
                <EditableField
                  value={invoice.clientName}
                  onChange={(v) => updateField('clientName', v)}
                  placeholder="Client name"
                  className="font-semibold text-gray-900"
                />
                <EditableField
                  value={invoice.clientCompany}
                  onChange={(v) => updateField('clientCompany', v)}
                  placeholder="Client company"
                  className="text-sm text-gray-700"
                />
                <AddressField
                  lines={invoice.clientAddress}
                  onChange={(v) => updateField('clientAddress', v)}
                  placeholders={['456 Oak Avenue, Suite 200', 'Los Angeles, CA 90001']}
                />
                <EditableField
                  value={invoice.clientEmail}
                  onChange={(v) => updateField('clientEmail', v)}
                  placeholder="client@email.com"
                  className="text-sm text-gray-700"
                />
              </div>

              <div className="max-w-[320px]">
                <div className="flex items-center gap-2 mb-2">
                  <label
                    className="text-xs font-bold uppercase tracking-wider"
                    style={{ color: invoice.accentColor }}
                  >
                    Ship To
                  </label>
                  <button
                    onClick={() => updateField('showShipTo', !invoice.showShipTo)}
                    className={`text-xs px-2 py-0.5 rounded-full transition-colors ${
                      invoice.showShipTo
                        ? 'bg-brand-blue text-white'
                        : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                    }`}
                  >
                    {invoice.showShipTo ? 'On' : 'Off'}
                  </button>
                </div>
                {invoice.showShipTo && (
                  <>
                    <EditableField
                      value={invoice.shipToName}
                      onChange={(v) => updateField('shipToName', v)}
                      placeholder="Recipient name"
                      className="font-semibold text-gray-900"
                    />
                    <AddressField
                      lines={invoice.shipToAddress}
                      onChange={(v) => updateField('shipToAddress', v)}
                      placeholders={['789 Elm Street', 'Chicago, IL 60601']}
                    />
                  </>
                )}
              </div>
            </div>

            {/* Line items table */}
            <div className="mb-8">
              <table className="w-full">
                <thead>
                  <tr style={{ backgroundColor: invoice.accentColor }}>
                    <th className="w-8" />
                    <th className="text-left text-white text-xs font-semibold uppercase tracking-wider py-3 px-3">
                      Description
                    </th>
                    <th className="text-right text-white text-xs font-semibold uppercase tracking-wider py-3 px-3 w-24">
                      Qty
                    </th>
                    <th className="text-right text-white text-xs font-semibold uppercase tracking-wider py-3 px-3 w-32">
                      Unit Price
                    </th>
                    <th className="text-right text-white text-xs font-semibold uppercase tracking-wider py-3 px-3 w-32">
                      Amount
                    </th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {invoice.lineItems.map((item, index) => {
                    const amount = (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0);
                    return (
                      <tr
                        key={item.id}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragEnter={() => handleDragEnter(index)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => e.preventDefault()}
                        className={`border-b border-gray-100 ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                        }`}
                      >
                        <td className="py-2 px-1">
                          <GripVertical
                            size={14}
                            className="text-gray-300 cursor-grab"
                          />
                        </td>
                        <td className="py-2 px-3">
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                            placeholder="Item description"
                            className="w-full bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-brand-blue focus:ring-0 text-sm text-gray-900 p-0 py-1"
                          />
                        </td>
                        <td className="py-2 px-3">
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateLineItem(item.id, 'quantity', e.target.value)}
                            min="0"
                            step="1"
                            className="w-full bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-brand-blue focus:ring-0 text-sm text-gray-900 text-right p-0 py-1"
                          />
                        </td>
                        <td className="py-2 px-3">
                          <input
                            type="number"
                            value={item.unitPrice}
                            onChange={(e) => updateLineItem(item.id, 'unitPrice', e.target.value)}
                            min="0"
                            step="0.01"
                            className="w-full bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-brand-blue focus:ring-0 text-sm text-gray-900 text-right p-0 py-1"
                          />
                        </td>
                        <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">
                          {formatCurrency(amount)}
                        </td>
                        <td className="py-2 px-1">
                          <button
                            onClick={() => removeLineItem(item.id)}
                            className="p-1 text-gray-400 hover:text-accent-red rounded transition-colors"
                            title="Remove item"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <button
                onClick={addLineItem}
                className="flex items-center gap-1.5 mt-3 px-3 py-2 text-sm text-brand-blue hover:bg-brand-light/50 rounded-lg transition-colors"
              >
                <Plus size={16} />
                Add Line Item
              </button>
            </div>

            {/* Totals section */}
            <div className="flex justify-end mb-10">
              <div className="w-full max-w-sm space-y-2">
                <TotalRow label="Subtotal" value={formatCurrency(totals.subtotal)} />

                {/* Discount toggle */}
                <ToggleRow
                  label="Discount"
                  enabled={invoice.showDiscount}
                  onToggle={() => updateField('showDiscount', !invoice.showDiscount)}
                >
                  <div className="flex items-center gap-2">
                    <select
                      value={invoice.discountType}
                      onChange={(e) => updateField('discountType', e.target.value)}
                      className="text-xs border border-gray-200 rounded px-1 py-0.5 bg-white focus:ring-0 focus:border-brand-blue"
                    >
                      <option value="percentage">%</option>
                      <option value="fixed">$</option>
                    </select>
                    <input
                      type="number"
                      value={invoice.discountValue}
                      onChange={(e) => updateField('discountValue', e.target.value)}
                      min="0"
                      step="0.01"
                      className="w-20 text-right text-sm border border-gray-200 rounded px-2 py-0.5 focus:ring-0 focus:border-brand-blue"
                    />
                    <span className="text-sm text-accent-red font-medium ml-auto">
                      -{formatCurrency(totals.discountAmount)}
                    </span>
                  </div>
                </ToggleRow>

                {/* Tax toggle */}
                <ToggleRow
                  label="Tax"
                  enabled={invoice.showTax}
                  onToggle={() => updateField('showTax', !invoice.showTax)}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={invoice.taxRate}
                      onChange={(e) => updateField('taxRate', e.target.value)}
                      min="0"
                      step="0.01"
                      className="w-20 text-right text-sm border border-gray-200 rounded px-2 py-0.5 focus:ring-0 focus:border-brand-blue"
                    />
                    <span className="text-xs text-gray-500">%</span>
                    <span className="text-sm text-gray-900 font-medium ml-auto">
                      {formatCurrency(totals.taxAmount)}
                    </span>
                  </div>
                </ToggleRow>

                {/* Shipping toggle — editable label */}
                <ToggleRow
                  label={invoice.shippingLabel}
                  enabled={invoice.showShipping}
                  onToggle={() => updateField('showShipping', !invoice.showShipping)}
                  editableLabel
                  onLabelChange={(v) => updateField('shippingLabel', v)}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={invoice.shippingAmount}
                      onChange={(e) => updateField('shippingAmount', e.target.value)}
                      min="0"
                      step="0.01"
                      className="w-20 text-right text-sm border border-gray-200 rounded px-2 py-0.5 focus:ring-0 focus:border-brand-blue"
                    />
                    <span className="text-sm text-gray-900 font-medium ml-auto">
                      {formatCurrency(totals.shipping)}
                    </span>
                  </div>
                </ToggleRow>

                <div className="border-t-2 border-gray-900 pt-2">
                  <TotalRow
                    label="Total"
                    value={formatCurrency(totals.total)}
                    bold
                    large
                  />
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Amount Paid</span>
                  <input
                    type="number"
                    value={invoice.amountPaid}
                    onChange={(e) => updateField('amountPaid', e.target.value)}
                    min="0"
                    step="0.01"
                    className="w-28 text-right border border-gray-200 rounded px-2 py-1 text-sm focus:ring-0 focus:border-brand-blue"
                  />
                </div>

                <div
                  className="flex items-center justify-between px-3 py-2 rounded-lg"
                  style={{ backgroundColor: `${invoice.accentColor}15` }}
                >
                  <span className="font-bold text-gray-900">Balance Due</span>
                  <span
                    className="text-lg font-bold"
                    style={{ color: invoice.accentColor }}
                  >
                    {formatCurrency(totals.balanceDue)}
                  </span>
                </div>
              </div>
            </div>

            {/* Footer section — styled to match PDF output */}
            <div className="border-t border-gray-200 pt-8 space-y-5">
              <FooterSection
                label="Notes"
                accentColor={invoice.accentColor}
                value={invoice.notes}
                onChange={(v) => updateField('notes', v)}
                placeholder="Thank you for your business!"
              />
              <FooterSection
                label="Payment Instructions"
                accentColor={invoice.accentColor}
                value={invoice.paymentInstructions}
                onChange={(v) => updateField('paymentInstructions', v)}
                placeholder="Bank details, PayPal, Venmo, etc."
              />
              <FooterSection
                label="Terms & Conditions"
                accentColor={invoice.accentColor}
                value={invoice.termsAndConditions}
                onChange={(v) => updateField('termsAndConditions', v)}
                placeholder="Payment terms, late fees, etc."
                small
              />
            </div>
          </div>
        </div>
      </div>
      <FreeTierBanner open={showFreeTierModal} onClose={() => setShowFreeTierModal(false)} />
    </div>
    </ProGate>
  );
}

/* ── Helper components ─────────────────────────────────────────────── */

function EditableField({ value, onChange, placeholder, className = '' }) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-brand-blue focus:ring-0 p-0 py-0.5 placeholder:text-gray-300 ${className}`}
    />
  );
}

function AddressField({ lines, onChange, placeholders }) {
  const arr = Array.isArray(lines) ? lines : ['', ''];

  const updateLine = (index, value) => {
    const next = [...arr];
    next[index] = value;
    onChange(next);
  };

  const addLine = () => {
    onChange([...arr, '']);
  };

  const removeLine = (index) => {
    if (arr.length <= 1) return;
    onChange(arr.filter((_, i) => i !== index));
  };

  return (
    <div className="max-w-[300px]">
      {arr.map((line, i) => (
        <div key={i} className="flex items-center gap-1 group/addr">
          <input
            type="text"
            value={line}
            onChange={(e) => updateLine(i, e.target.value)}
            placeholder={placeholders[i] || `Address line ${i + 1}`}
            className="flex-1 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-brand-blue focus:ring-0 p-0 py-0.5 placeholder:text-gray-300 text-sm text-gray-700"
          />
          {arr.length > 1 && (
            <button
              onClick={() => removeLine(i)}
              className="p-0.5 text-gray-300 hover:text-accent-red opacity-0 group-hover/addr:opacity-100 transition-opacity"
              title="Remove line"
            >
              <X size={12} />
            </button>
          )}
        </div>
      ))}
      <button
        onClick={addLine}
        className="flex items-center gap-1 mt-0.5 text-xs text-gray-400 hover:text-brand-blue transition-colors"
      >
        <Plus size={12} />
        <span>Add line</span>
      </button>
    </div>
  );
}

function ResizableLogo({ logo, onRemove, onResize }) {
  const containerRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const startRef = useRef(null);

  const width = logo.width || 160;
  const height = logo.height || 64;

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
    startRef.current = { x: e.clientX, y: e.clientY, w: width, h: height };
  }, [width, height]);

  useEffect(() => {
    if (!dragging) return;
    const handleMouseMove = (e) => {
      const s = startRef.current;
      const dx = e.clientX - s.x;
      const aspect = s.w / s.h;
      const newW = Math.max(40, Math.min(300, s.w + dx));
      const newH = newW / aspect;
      onResize(Math.round(newW), Math.round(newH));
    };
    const handleMouseUp = () => setDragging(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, onResize]);

  return (
    <div ref={containerRef} className="relative inline-block group" style={{ width, height }}>
      <img
        src={logo.dataUrl}
        alt="Company logo"
        className="w-full h-full object-contain"
        draggable={false}
      />
      {/* Corner resize handles */}
      <div
        onMouseDown={handleMouseDown}
        className="absolute -bottom-1 -right-1 w-3 h-3 bg-white border-2 border-brand-blue rounded-sm cursor-se-resize opacity-0 group-hover:opacity-100 transition-opacity"
      />
      <div className="absolute -top-1 -left-1 w-3 h-3 bg-white border-2 border-brand-blue rounded-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
      <div className="absolute -top-1 -right-1 w-3 h-3 bg-white border-2 border-brand-blue rounded-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
      <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-white border-2 border-brand-blue rounded-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
      {/* Remove button */}
      <button
        onClick={onRemove}
        className="absolute -top-2 -right-2 p-1 bg-white border border-gray-200 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity z-10"
      >
        <X size={12} className="text-gray-500" />
      </button>
      {/* Selection border */}
      <div className="absolute inset-0 border border-brand-blue/40 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
    </div>
  );
}

function FooterSection({ label, accentColor, value, onChange, placeholder, small = false }) {
  return (
    <div>
      <label
        className="text-xs font-bold uppercase tracking-wider mb-1 block"
        style={{ color: accentColor }}
      >
        {label}
      </label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={2}
        className={`w-full text-gray-700 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-brand-blue focus:ring-0 p-0 py-0.5 resize-none placeholder:text-gray-300 ${small ? 'text-xs' : 'text-sm'}`}
      />
    </div>
  );
}

function TotalRow({ label, value, bold = false, large = false }) {
  return (
    <div className={`flex items-center justify-between ${large ? 'text-lg' : 'text-sm'}`}>
      <span className={`${bold ? 'font-bold text-gray-900' : 'text-gray-600'}`}>{label}</span>
      <span className={`${bold ? 'font-bold text-gray-900' : 'text-gray-900'}`}>{value}</span>
    </div>
  );
}

function ToggleRow({ label, enabled, onToggle, children, editableLabel = false, onLabelChange }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        {editableLabel && enabled ? (
          <input
            type="text"
            value={label}
            onChange={(e) => onLabelChange(e.target.value)}
            className="text-sm text-gray-600 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-brand-blue focus:ring-0 p-0 w-24"
          />
        ) : (
          <span className="text-sm text-gray-600">{label}</span>
        )}
        <button
          onClick={onToggle}
          className={`text-xs px-2 py-0.5 rounded-full transition-colors ${
            enabled
              ? 'bg-brand-blue text-white'
              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
          }`}
        >
          {enabled ? 'On' : 'Off'}
        </button>
      </div>
      {enabled && children}
    </div>
  );
}
